import { useState } from 'react';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  FileTextOutlined,
  LockOutlined,
  PauseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Link2, Plus, RefreshCw } from 'lucide-react';

import { DataTable, MenuThaoTac, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { KHONG_CO, ngayGioVN, ngayVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { LOI_DNS_DIA_CHI_RIENG, TRANG_THAI_DIA_CHI_RIENG } from '../constants';
import {
  useDiaChiRieng,
  useDoiTrangThaiDiaChi,
  useKiemDnsDiaChi,
  useThemDiaChi,
} from '../hooks/useDiaChiRieng';
import type { ThaoTacTrangThai } from '../hooks/useDiaChiRieng';
import { banGhiCanTao, canHuongDanDns, thaoTacDuoc } from '../nghiep-vu/dia-chi-rieng';
import type { CustomHost } from '../types';
import { OBanGhi, useSaoChep } from './BanGhiDns';
import { HopThemDiaChi } from './HopThemDiaChi';

/** Chuỗi của hộp xác nhận theo thao tác; tạm ngưng ở cổng nền tảng nói thêm về khoá. */
const HOP_XAC_NHAN: Record<ThaoTacTrangThai, { tieuDe: KhoaDich; than: KhoaDich; nut: KhoaDich; nguyHiem: boolean }> = {
  kichHoat: {
    tieuDe: 'diaChiRieng.xnKichHoatTieuDe',
    than: 'diaChiRieng.xnKichHoatThan',
    nut: 'diaChiRieng.xnKichHoatNut',
    nguyHiem: false,
  },
  tamNgung: {
    tieuDe: 'diaChiRieng.xnTamNgungTieuDe',
    than: 'diaChiRieng.xnTamNgungThan',
    nut: 'diaChiRieng.xnTamNgungNut',
    nguyHiem: false,
  },
  khoa: { tieuDe: 'diaChiRieng.xnKhoaTieuDe', than: 'diaChiRieng.xnKhoaThan', nut: 'diaChiRieng.xnKhoaNut', nguyHiem: false },
  go: { tieuDe: 'diaChiRieng.xnGoTieuDe', than: 'diaChiRieng.xnGoThan', nut: 'diaChiRieng.xnGoNut', nguyHiem: true },
};

/** Chứng chỉ sắp hết hạn trong số ngày này thì tô cảnh báo (ADR-0097, S-8). */
const NGAY_CANH_BAO_CHUNG_CHI = 14;

/** Ô chứng chỉ: hạn dùng, cảnh báo khi sắp hết; domain đang dùng mà chưa có chứng chỉ thì nói lúc nào được cấp. */
function OChungChi({ h }: { h: CustomHost }) {
  const { t } = useT();
  if (h.cert_expires_at) {
    const conNgay = (new Date(h.cert_expires_at).getTime() - Date.now()) / 86_400_000;
    const sapHet = conNgay < NGAY_CANH_BAO_CHUNG_CHI;
    return (
      <span className={sapHet ? 'text-xs font-medium text-warning-700' : 'text-xs text-content-secondary'}>
        {t(sapHet ? 'diaChiRieng.chungChiSapHet' : 'diaChiRieng.chungChiHan', { ngay: ngayVN(h.cert_expires_at) })}
      </span>
    );
  }
  return (
    <span className="text-xs text-content-muted">
      {h.status === 'DANG_DUNG' ? t('diaChiRieng.chungChiChoCap') : KHONG_CO}
    </span>
  );
}

/**
 * Khối bản ghi DNS khách cần tạo cho một domain (ADR-0097 mục 3): TXT chứng minh sở hữu, rồi CNAME về đích của
 * nền tảng với subdomain — hoặc A/AAAA với domain gốc, kèm ghi chú ALIAS/ANAME (CNAME flattening) về cùng đích. Kèm
 * lý do lần kiểm gần nhất chưa đạt và nút kiểm lại (người sửa được).
 */
function KhoiDns({
  h,
  duocKiem,
  dangKiem,
  onKiem,
}: {
  h: CustomHost;
  duocKiem: boolean;
  dangKiem: boolean;
  onKiem: () => void;
}) {
  const { t } = useT();
  const { daChep, chep } = useSaoChep();
  const banGhi = banGhiCanTao(h);

  return (
    <section
      aria-label={t('diaChiRieng.dnsKhoiNhan', { host: h.hostname })}
      className="rounded-lg border border-default bg-surface p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="break-all text-sm font-semibold text-content-primary">
            {t('diaChiRieng.dnsTieuDe', { host: h.hostname })}
          </h3>
          <p className="mt-0.5 text-xs leading-relaxed text-content-muted">
            {t(h.apex ? 'diaChiRieng.dnsMoGoc' : 'diaChiRieng.dnsMo')}
          </p>
        </div>
        {duocKiem && (
          <Button variant="outline" size="sm" loading={dangKiem} onClick={onKiem}>
            {!dangKiem && <RefreshCw size={14} />}
            {dangKiem ? t('diaChiRieng.dangKiem') : t('diaChiRieng.kiemLai')}
          </Button>
        )}
      </div>

      {h.apex && h.target_ips.length === 0 && (
        <p role="status" className="mt-3 rounded bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
          {t('diaChiRieng.dnsThieuIp')}
        </p>
      )}

      {h.apex && h.cname_target && (
        <p className="mt-3 text-xs leading-relaxed text-content-secondary">
          {t('diaChiRieng.dnsGhiChuAlias', { dich: h.cname_target })}
        </p>
      )}

      {h.dns_error && (
        <p role="status" className="mt-3 rounded bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
          {t(LOI_DNS_DIA_CHI_RIENG[h.dns_error])}
        </p>
      )}

      <div className="mt-3 space-y-3">
        {banGhi.map((b, i) => (
          <div key={`${b.loai}-${b.giaTri}`} className="rounded border border-default px-3">
            <p className="pt-2.5 text-xs font-semibold text-content-secondary">
              {t('diaChiRieng.banGhiLoai', { loai: b.loai })}
            </p>
            <OBanGhi
              nhan={t('tenant.dnsTen')}
              giaTri={b.ten}
              daChep={daChep === `${i}-ten`}
              onChep={() => chep(`${i}-ten`, b.ten)}
            />
            <OBanGhi
              nhan={t('tenant.dnsGiaTri')}
              giaTri={b.giaTri}
              daChep={daChep === `${i}-gia-tri`}
              onChep={() => chep(`${i}-gia-tri`, b.giaTri)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * "Địa chỉ truy cập riêng" — domain thương hiệu của một tenant (ADR-0097): người dùng của tenant vào cổng tổ chức
 * bằng domain của mình, nền tảng phục vụ ngầm. Cùng thao tác (thêm, kiểm DNS, kích hoạt, tạm ngưng, gỡ) ở hai nơi
 * (ADR-0097 mục 2, 7):
 * - tab Domain của chi tiết tenant, cổng nền tảng (`laNenTang`): Platform Admin hỗ trợ — `duocSua` là
 *   `TENANT.UPDATE` và tenant chưa ARCHIVED/DELETED. Platform Admin tạm ngưng thì domain bị khoá; domain tổ chức đã
 *   tự tạm ngưng có thêm mục "Khoá địa chỉ";
 * - màn `/he-thong/dia-chi-rieng` của cổng tổ chức: Tenant Admin tự làm; domain bị khoá thì ẩn "Kích hoạt" và "Gỡ",
 *   nói phải liên hệ quản trị nền tảng. Màn đã có tiêu đề trang nên khung không lặp tiêu đề, chỉ giữ nút thêm.
 */
export function DiaChiRieng({
  tenantId,
  duocSua,
  laNenTang,
}: {
  tenantId: string;
  duocSua: boolean;
  laNenTang: boolean;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const q = useDiaChiRieng(tenantId);
  const them = useThemDiaChi(tenantId);
  const kiem = useKiemDnsDiaChi(tenantId);
  const doi = useDoiTrangThaiDiaChi(tenantId);
  const [hopThem, setHopThem] = useState({ mo: false, lan: 0 });
  const [xacNhan, setXacNhan] = useState<{ loai: ThaoTacTrangThai; host: CustomHost } | null>(null);
  // Địa chỉ đã xác minh vẫn mở lại được khối bản ghi DNS (gửi lại cho khách, đổi nhà cung cấp DNS)
  const [xemDns, setXemDns] = useState<ReadonlySet<string>>(new Set());
  const batTatDns = (id: string) =>
    setXemDns((cu) => {
      const moi = new Set(cu);
      if (!moi.delete(id)) moi.add(id);
      return moi;
    });
  /*
   * `Idempotency-Key` theo từng hostname gửi đi: bấm lại sau lỗi mạng dùng lại khoá nên không thêm hai lần, còn
   * sửa sang hostname khác thì phải khoá mới — cùng khoá mà khác thân thì backend trả `422 IDEMPOTENCY_KEY_REUSED`.
   */
  const [khoaLap, setKhoaLap] = useState({ host: '', khoa: '' });

  const ds = q.data?.items ?? [];
  const toiDa = q.data?.max_hosts ?? 3;
  const conCho = ds.length < toiDa;
  // Chưa có danh sách (đang tải, lỗi) thì chưa biết còn chỗ: không mời thêm rồi rút lại
  const duocThem = duocSua && q.data !== undefined && conCho;

  const moHopThem = () => {
    them.reset();
    setHopThem((h) => ({ mo: true, lan: h.lan + 1 }));
  };

  const guiThem = (hostname: string) => {
    const khoa = khoaLap.host === hostname ? khoaLap.khoa : crypto.randomUUID();
    setKhoaLap({ host: hostname, khoa });
    them.mutate(
      { hostname, khoaLap: khoa },
      {
        onSuccess: () => {
          setHopThem((h) => ({ ...h, mo: false }));
          setKhoaLap({ host: '', khoa: '' });
        },
      },
    );
  };

  const kiemDns = (h: CustomHost) =>
    kiem.mutate(h.id, { onError: (err) => toast.error(t('diaChiRieng.kiemLoi', { loi: dichLoi(err) })) });

  const moXacNhan = (loai: ThaoTacTrangThai, host: CustomHost) => {
    doi.reset();
    setXacNhan({ loai, host });
  };

  const cot: TableProps<CustomHost>['columns'] = [
    {
      title: t('diaChiRieng.cotHost'),
      dataIndex: 'hostname',
      width: 260,
      render: (v: string, row) => (
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="break-all font-mono text-sm text-content-primary">{v}</span>
          {row.apex && <Badge variant="info">{t('diaChiRieng.nhanGoc')}</Badge>}
        </span>
      ),
    },
    {
      title: t('diaChiRieng.cotTrangThai'),
      dataIndex: 'status',
      width: 150,
      render: (v: CustomHost['status'], row) => (
        <span className="flex min-w-0 flex-col items-start gap-1">
          <Badge variant={TRANG_THAI_DIA_CHI_RIENG[v].variant}>{t(TRANG_THAI_DIA_CHI_RIENG[v].khoa)}</Badge>
          {row.platform_locked && (
            <span className="text-2xs leading-snug text-content-muted">
              {t(laNenTang ? 'diaChiRieng.khoaNenTang' : 'diaChiRieng.khoaToChuc')}
            </span>
          )}
        </span>
      ),
    },
    {
      title: t('diaChiRieng.cotDns'),
      width: 220,
      render: (_v, row) => (
        <span className="flex min-w-0 flex-col gap-0.5 text-xs">
          <span className={row.dns_error ? 'font-medium text-warning-700' : 'text-content-secondary'}>
            {row.dns_error
              ? t('diaChiRieng.dnsChuaDatNgan')
              : row.dns_checked_at
                ? t('diaChiRieng.dnsDatNgan')
                : t('diaChiRieng.dnsChuaKiem')}
          </span>
          {row.dns_checked_at && (
            <span className="text-content-muted">{ngayGioVN(row.dns_checked_at, locale)}</span>
          )}
        </span>
      ),
    },
    {
      title: t('diaChiRieng.cotChungChi'),
      width: 190,
      render: (_v, row) => <OChungChi h={row} />,
    },
    ...(duocSua
      ? [
          {
            title: t('chung.thaoTac'),
            key: 'thao-tac',
            width: 72,
            align: 'center' as const,
            render: (_v: unknown, row: CustomHost) => {
              const duoc = thaoTacDuoc(row, laNenTang);
              return (
                <MenuThaoTac
                  tenBanGhi={row.hostname}
                  muc={[
                    {
                      khoa: 'kiem',
                      nhan: t('diaChiRieng.kiemLai'),
                      icon: <SyncOutlined />,
                      hien: duoc.kiemDns,
                      nhom: 'xem',
                      onChon: () => kiemDns(row),
                    },
                    {
                      khoa: 'ban-ghi-dns',
                      nhan: t(xemDns.has(row.id) ? 'diaChiRieng.anBanGhiDns' : 'diaChiRieng.xemBanGhiDns'),
                      icon: <FileTextOutlined />,
                      hien: row.status !== 'DA_GO' && !canHuongDanDns(row),
                      nhom: 'xem',
                      onChon: () => batTatDns(row.id),
                    },
                    {
                      khoa: 'kich-hoat',
                      nhan: t('diaChiRieng.kichHoat'),
                      icon: <CheckCircleOutlined />,
                      hien: duoc.kichHoat,
                      onChon: () => moXacNhan('kichHoat', row),
                    },
                    {
                      khoa: 'tam-ngung',
                      nhan: t('diaChiRieng.tamNgung'),
                      icon: <PauseCircleOutlined />,
                      hien: duoc.tamNgung,
                      onChon: () => moXacNhan('tamNgung', row),
                    },
                    {
                      khoa: 'khoa',
                      nhan: t('diaChiRieng.khoa'),
                      icon: <LockOutlined />,
                      hien: duoc.khoa,
                      onChon: () => moXacNhan('khoa', row),
                    },
                    {
                      khoa: 'go',
                      nhan: t('diaChiRieng.go'),
                      icon: <DeleteOutlined />,
                      hien: duoc.go,
                      nhom: 'pha-huy',
                      onChon: () => moXacNhan('go', row),
                    },
                  ]}
                />
              );
            },
          },
        ]
      : []),
  ];

  const canDns = ds.filter((h) => canHuongDanDns(h) || (h.status !== 'DA_GO' && xemDns.has(h.id)));
  const hostXacNhan = xacNhan?.host.hostname ?? '';
  const chuoiXacNhan = HOP_XAC_NHAN[xacNhan?.loai ?? 'go'];
  const nutThem = duocThem && (
    <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={moHopThem}>
      {t('diaChiRieng.them')}
    </Button>
  );
  const daDu = duocSua && !conCho && (
    <p role="status" className="min-w-0 text-xs text-content-muted">
      {t('diaChiRieng.daDu', { toiDa })}
    </p>
  );

  return (
    <Tile className="overflow-hidden">
      {laNenTang ? (
        <>
          <TileHeader icon={<Link2 size={15} />} tone="sky" title={t('diaChiRieng.tieuDe')} action={nutThem} />
          {daDu && <div className="border-b border-default px-5 py-2.5">{daDu}</div>}
        </>
      ) : (
        // Chưa có địa chỉ thì trạng thái rỗng đã mời thêm; không đặt thêm nút thứ hai ở trên
        ds.length > 0 &&
        (nutThem || daDu) && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-default px-5 py-3">
            {daDu || <span />}
            {nutThem}
          </div>
        )
      )}

      {q.error && !q.data ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <span className="min-w-0 text-sm text-danger-700">{t('diaChiRieng.loiTai', { loi: dichLoi(q.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            <RefreshCw size={14} />
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <DataTable<CustomHost>
          caption={t('diaChiRieng.caption')}
          rowKey="id"
          dataSource={ds}
          columns={cot}
          loading={q.isLoading}
          size="middle"
          // Không dùng `max-content`: hostname dài sẽ kéo cột ra hết chiều rộng và đẩy cột trạng thái khỏi màn.
          // Bề rộng cố định cho cột hostname tự xuống dòng (`break-all`), bảng chỉ cuộn ngang khi hẹp hơn mức này.
          scroll={{ x: duocSua ? 820 : 740 }}
          pagination={false}
          locale={{
            emptyText: q.isLoading ? (
              ' '
            ) : (
              <Empty
                icon={Link2}
                title={t('diaChiRieng.rong')}
                description={t(laNenTang ? 'diaChiRieng.rongMoNenTang' : 'diaChiRieng.rongMoToChuc')}
                action={
                  duocThem ? (
                    <Button variant="primary" size="md" icon={<Plus size={15} />} onClick={moHopThem}>
                      {t('diaChiRieng.them')}
                    </Button>
                  ) : undefined
                }
              />
            ),
          }}
        />
      )}

      {canDns.length > 0 && (
        <TileBody className="space-y-3 border-t border-default">
          {canDns.map((h) => (
            <KhoiDns
              key={h.id}
              h={h}
              duocKiem={duocSua}
              dangKiem={kiem.isPending && kiem.variables === h.id}
              onKiem={() => kiemDns(h)}
            />
          ))}
        </TileBody>
      )}

      {duocSua && (
        <HopThemDiaChi
          key={`them-${hopThem.lan}`}
          open={hopThem.mo}
          dangGui={them.isPending}
          loi={them.error}
          onClose={() => setHopThem((h) => ({ ...h, mo: false }))}
          onSua={() => them.reset()}
          onThem={guiThem}
          toiDa={toiDa}
          laNenTang={laNenTang}
        />
      )}
      {duocSua && (
        <HopXacNhanThaoTac
          open={xacNhan !== null}
          tieuDe={t(chuoiXacNhan.tieuDe, { host: hostXacNhan })}
          than={t(
            xacNhan?.loai === 'tamNgung' && laNenTang ? 'diaChiRieng.xnTamNgungThanNenTang' : chuoiXacNhan.than,
            { host: hostXacNhan },
          )}
          nhanXacNhan={t(chuoiXacNhan.nut)}
          nguyHiem={chuoiXacNhan.nguyHiem}
          dangChay={doi.isPending}
          loi={doi.error ? dichLoi(doi.error) : null}
          onClose={() => setXacNhan(null)}
          onXacNhan={() => {
            if (xacNhan) doi.mutate(xacNhan, { onSuccess: () => setXacNhan(null) });
          }}
        />
      )}
    </Tile>
  );
}
